import React, { useState } from 'react';
import {
  Drawer,
  DrawerHeader,
  DrawerBody,
  Box,
  Heading,
  Text,
  Button,
  Alert,
  Badge,
  RefreshIcon,
  Link,
} from '@razorpay/blade/components';
import { useWorkflowDetailsView } from '../useWorkflowDetailsView';
import { capitalizeFirstLetter } from 'utils/Strings';
import employeeBankAccountApis, { GET_BANK_ACCOUNT_REQUESTS } from 'api/employeeBankAccount';
import { useQuery } from 'react-query';
import {
  DetailsFooter,
  DetailsViewSkeleton,
} from 'components/SalaryComponents/components/details/DetailsViewHelpers';
import { isAccountUnverified } from './utils';
import { AppError, getAppErrorDetails } from 'utils/AppError';
import { errorParser } from 'utils/Api';
import { ApproveModal } from './components/ApproveModal';
import { RejectModal } from './components/RejectModal';

const bankProofTypes = {
  cheque: 'Cancelled Cheque',
  bank_statement: 'Bank Statement',
} as const;

export const DetailsView = () => {
  const [approvalAction, setApprovalAction] = useState<'approve' | 'reject'>();
  const { closeDetailsView, workflowRequestId } = useWorkflowDetailsView();

  const { data, isLoading, isFetching, isError, refetch, error } = useQuery({
    queryKey: [GET_BANK_ACCOUNT_REQUESTS, workflowRequestId],
    queryFn: () =>
      employeeBankAccountApis.getBankAccountRequestDetails({
        bank_account_id: workflowRequestId || '',
      }),
    enabled: !!workflowRequestId,
    refetchOnMount: true,
    onError: (e: AppError) => {
      //
    },
  });

  const shouldAddConnector = data?.email && data?.mobile;

  const subtitle =
    data?.email || data?.mobile
      ? `${data?.email || ''}${shouldAddConnector ? ' • ' : ''}${data?.mobile || ''}`
      : undefined;

  const onDismissApprovalModal = () => {
    setApprovalAction(undefined);
  };

  const openApproveModal = () => {
    setApprovalAction('approve');
  };

  const openRejectModal = () => {
    setApprovalAction('reject');
  };

  const renderBody = () => {
    if (isLoading || isFetching) {
      return <DetailsViewSkeleton />;
    }

    if (isError) {
      const errorMessage = errorParser(getAppErrorDetails(error))[0];
      return (
        <Box
          height={'100%'}
          display={'flex'}
          flexDirection={'column'}
          alignItems={'center'}
          gap={'spacing.5'}
          justifyContent={'center'}>
          <Text color={'surface.text.gray.muted'} weight={'medium'} textAlign={'center'}>
            {errorMessage}
          </Text>
          <Button icon={RefreshIcon} size={'small'} onClick={refetch}>
            Retry
          </Button>
        </Box>
      );
    }

    if (data) {
      return (
        <Box display="flex" flexDirection="column" height="100%">
          <Box display="flex" gap="spacing.4" marginBottom="spacing.5">
            <Box
              width="45%"
              borderLeftColor="surface.border.gray.muted"
              borderLeftWidth="thicker"
              paddingLeft="spacing.4">
              <Text size="xsmall" weight="semibold">
                Employee Name
              </Text>
              <Text size="small">{data.employee_name}</Text>
            </Box>
            <Box
              borderLeftColor="surface.border.gray.muted"
              borderLeftWidth="thicker"
              paddingLeft="spacing.4">
              <Text size="xsmall" weight="semibold">
                Account Holder Name
              </Text>
              <Text size="small">{data.beneficiary_name ?? '--'}</Text>
            </Box>
          </Box>
          {Boolean(data.verification_status === 'mismatch') && (
            <Alert
              description="Account holder name does not match the employee's name."
              color="notice"
              isFullWidth={true}
              isDismissible={false}
              marginBottom="spacing.5"
            />
          )}
          {Boolean(isAccountUnverified(data.verification_status) && data.attachment) && (
            <Alert
              description="Bank account details are not verified. Please manually verify them using the provided bank proof below."
              color="notice"
              isFullWidth={true}
              isDismissible={false}
              marginBottom="spacing.5"
            />
          )}

          <Box marginBottom="spacing.6" flexGrow={1}>
            <Heading size="small" weight="semibold">
              Request Details
            </Heading>

            <Box marginTop="spacing.4">
              <Box display="flex" gap="spacing.4" marginBottom="spacing.3">
                <Box width="45%">
                  <Text color="surface.text.gray.muted" size="medium">
                    Account Number
                  </Text>
                </Box>
                <Text size="medium">{data.account_number}</Text>
              </Box>
              <Box display="flex" gap="spacing.4" marginBottom="spacing.3">
                <Box width="45%">
                  <Text color="surface.text.gray.muted" size="medium">
                    IFSC Code
                  </Text>
                </Box>
                <Text size="medium">{data.ifsc}</Text>
              </Box>
              <Box display="flex" gap="spacing.4">
                <Box width="45%">
                  <Text color="surface.text.gray.muted" size="medium">
                    Account Holder Name
                  </Text>
                </Box>
                <Text size="medium">{data.beneficiary_name ?? '--'}</Text>
              </Box>
              {Boolean(data.attachment) && (
                <Box display="flex" gap="spacing.4" marginTop="spacing.3">
                  <Box width="45%">
                    <Text color="surface.text.gray.muted" weight="regular" size="medium">
                      Bank Proof
                    </Text>
                  </Box>
                  <Text weight="regular" color="surface.text.gray.subtle" size="medium">
                    {bankProofTypes[data.attachment_type]}
                    <Link marginLeft="spacing.2" href={data.attachment} target="_blank">
                      View
                    </Link>
                  </Text>
                </Box>
              )}
            </Box>
          </Box>

          <DetailsFooter isSticky={true} padding="spacing.0">
            <Box display="flex" gap="spacing.3" width="100%">
              <Button
                variant="secondary"
                size="small"
                color="negative"
                isFullWidth
                onClick={openRejectModal}>
                Reject
              </Button>
              <Button variant="primary" size="small" isFullWidth onClick={openApproveModal}>
                Approve
              </Button>
            </Box>
          </DetailsFooter>
        </Box>
      );
    }
  };

  return (
    <>
      <Drawer
        isOpen={!!workflowRequestId}
        onDismiss={closeDetailsView}
        accessibilityLabel={`bank account request details view for ${data?.employee_name}`}>
        <DrawerHeader
          title={data?.employee_name || undefined}
          subtitle={subtitle}
          titleSuffix={
            data?.employee_type ? (
              <Badge color="neutral">{capitalizeFirstLetter(data.employee_type)}</Badge>
            ) : undefined
          }
        />
        <DrawerBody>{renderBody()}</DrawerBody>
      </Drawer>
      <ApproveModal
        isOpen={approvalAction === 'approve'}
        onDismiss={onDismissApprovalModal}
        workflowRequestId={workflowRequestId}
      />
      <RejectModal
        isOpen={approvalAction === 'reject'}
        onDismiss={onDismissApprovalModal}
        workflowRequestId={workflowRequestId}
      />
    </>
  );
};
